/**
 * Pulse Product Infrastructure
 * 
 * Single source of truth for product state (variant, quantity, availability)
 * and event system for all Pulse product blocks.
 * 
 * Architecture:
 * - Global namespace: window.PulseProduct
 * - Event bus: Custom events on document
 * - State management: Centralized product/variant/quantity state
 * - DOM hooks: Standardized selectors for interoperability
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION & CONSTANTS
  // ============================================================================

  const SELECTORS = {
    productRoot: '[data-pulse-product-root]',
    productForm: '[data-pulse-product-form]',
    quantityInput: '[name="quantity"]',
    variantInput: '[name="id"]',
    variantOptions: '[data-option-position]',
    variantsJson: '[data-pulse-variants-json]',
    pulseBlock: '[data-pulse-block]'
  };

  const EVENTS = {
    variantChange: 'pulse:variant:change',
    quantityChange: 'pulse:quantity:change',
    cartAdded: 'pulse:cart:added',
    cartError: 'pulse:cart:error'
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const state = {
    productId: null,
    selectedVariantId: null,
    selectedVariant: null,
    quantity: 1,
    variants: [],
    currencyFormat: null,
    moneyFormat: null,
    isAvailable: false,
    inventoryQty: null,
    inventoryTracked: false,
    initialized: false
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Throttle function for scroll/resize events
   */
  function throttle(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if prefers-reduced-motion is enabled
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Dispatch custom event on document
   */
  function dispatchEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Parse JSON from script tag safely
   */
  function parseJsonScript(scriptElement) {
    if (!scriptElement || !scriptElement.textContent) return null;
    try {
      return JSON.parse(scriptElement.textContent);
    } catch (e) {
      console.warn('PulseProduct: Failed to parse variants JSON', e);
      return null;
    }
  }

  // ============================================================================
  // MONEY FORMATTING
  // ============================================================================

  /**
   * Format money (cents) to currency string
   * Uses Shopify.formatMoney if available, else minimal formatter
   */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') {
      cents = parseFloat(cents) || 0;
    }

    // Use Shopify.formatMoney if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format || state.moneyFormat || '{{amount}}');
    }

    // Fallback: minimal formatter
    const value = (cents / 100).toFixed(2);
    const formatString = format || state.moneyFormat || '{{amount}}';
    
    return formatString.replace('{{amount}}', value);
  }

  // ============================================================================
  // VARIANT RESOLUTION
  // ============================================================================

  /**
   * Get selected option values from DOM
   * Supports both variant-radios and variant-selects
   */
  function getSelectedOptions() {
    const productRoot = getClosestProductRoot(document);
    if (!productRoot) return null;

    const variantRadios = productRoot.querySelector('variant-radios');
    const variantSelects = productRoot.querySelector('variant-selects');

    let options = [];

    if (variantRadios) {
      // Get checked radio inputs
      const fieldsets = variantRadios.querySelectorAll('fieldset');
      options = Array.from(fieldsets).map(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }).filter(val => val !== null);
    } else if (variantSelects) {
      // Get selected option values from selects
      const selects = variantSelects.querySelectorAll('select');
      options = Array.from(selects).map(select => select.value).filter(val => val);
    } else {
      // Fallback: try to find option inputs by data-option-position
      const optionInputs = productRoot.querySelectorAll('[data-option-position]');
      if (optionInputs.length > 0) {
        const sorted = Array.from(optionInputs).sort((a, b) => {
          const posA = parseInt(a.dataset.optionPosition || '0', 10);
          const posB = parseInt(b.dataset.optionPosition || '0', 10);
          return posA - posB;
        });
        options = sorted.map(input => {
          if (input.type === 'radio') {
            return input.checked ? input.value : null;
          }
          return input.value || null;
        }).filter(val => val !== null);
      }
    }

    return options.length > 0 ? options : null;
  }

  /**
   * Find variant by option values
   */
  function findVariantByOptions(options) {
    if (!options || !Array.isArray(options) || options.length === 0) {
      return null;
    }

    if (state.variants.length === 0) {
      return null;
    }

    // Match variant by option values
    const variant = state.variants.find(v => {
      const vOptions = [v.option1, v.option2, v.option3].filter(Boolean);
      if (vOptions.length !== options.length) return false;

      return options.every((opt, idx) => {
        return vOptions[idx] === opt;
      });
    });

    return variant || null;
  }

  /**
   * Find variant by ID
   */
  function findVariantById(variantId) {
    if (!variantId || state.variants.length === 0) return null;
    return state.variants.find(v => v.id === variantId) || null;
  }

  // ============================================================================
  // STATE UPDATES
  // ============================================================================

  /**
   * Update variant state
   */
  function updateVariantState(variant) {
    if (!variant) {
      state.selectedVariant = null;
      state.selectedVariantId = null;
      state.isAvailable = false;
      state.inventoryQty = null;
      state.inventoryTracked = false;
      return;
    }

    state.selectedVariant = variant;
    state.selectedVariantId = variant.id;
    state.isAvailable = variant.available === true;
    state.inventoryQty = variant.inventory_quantity !== undefined ? variant.inventory_quantity : null;
    state.inventoryTracked = variant.inventory_management !== null;

    // Update variant input in form
    const productForm = getProductForm();
    if (productForm) {
      const variantInput = productForm.querySelector(SELECTORS.variantInput);
      if (variantInput) {
        variantInput.value = variant.id;
      }
    }
  }

  /**
   * Update quantity state
   */
  function updateQuantityState(qty) {
    const quantity = parseInt(qty, 10) || 1;
    state.quantity = Math.max(1, quantity);

    // Update quantity input in form
    const productForm = getProductForm();
    if (productForm) {
      const quantityInput = productForm.querySelector(SELECTORS.quantityInput);
      if (quantityInput) {
        quantityInput.value = state.quantity;
      }
    }
  }

  // ============================================================================
  // DOM HELPERS
  // ============================================================================

  /**
   * Get closest product root element
   */
  function getClosestProductRoot(element) {
    if (!element) return null;
    
    // Try data attribute first
    let root = element.closest ? element.closest(SELECTORS.productRoot) : null;
    if (root) return root;

    // Fallback: find by section ID or product-info element
    const productInfo = element.closest ? element.closest('product-info') : null;
    if (productInfo) {
      const sectionId = productInfo.dataset.section;
      if (sectionId) {
        return document.querySelector(`#MainProduct-${sectionId}`) || 
               document.querySelector(`[data-section="${sectionId}"]`) ||
               productInfo;
      }
    }

    // Last resort: find any product root
    return document.querySelector(SELECTORS.productRoot);
  }

  /**
   * Get product form element
   */
  function getProductForm() {
    const productRoot = getClosestProductRoot(document);
    if (!productRoot) return null;

    // Try data attribute first
    let form = productRoot.querySelector(SELECTORS.productForm);
    if (form) return form;

    // Fallback: find form with cart/add action
    form = productRoot.querySelector('form[action*="/cart/add"]');
    if (form) return form;

    // Alternative: find product-form custom element
    form = productRoot.querySelector('product-form');
    if (form) {
      const innerForm = form.querySelector('form');
      if (innerForm) return innerForm;
    }

    return null;
  }

  /**
   * Load variants JSON from script tag
   */
  function loadVariantsJson() {
    const productRoot = getClosestProductRoot(document);
    if (!productRoot) return false;

    // Try data attribute first
    let script = productRoot.querySelector(SELECTORS.variantsJson);
    
    // Fallback: find in variant-radios or variant-selects
    if (!script) {
      const variantRadios = productRoot.querySelector('variant-radios');
      const variantSelects = productRoot.querySelector('variant-selects');
      const container = variantRadios || variantSelects;
      if (container) {
        script = container.querySelector('script[type="application/json"]');
      }
    }

    if (!script) {
      console.warn('PulseProduct: Variants JSON not found');
      return false;
    }

    const variants = parseJsonScript(script);
    if (!variants || !Array.isArray(variants)) {
      console.warn('PulseProduct: Invalid variants JSON');
      return false;
    }

    state.variants = variants;

    // Try to get product ID from first variant
    if (variants.length > 0 && variants[0].product_id) {
      state.productId = variants[0].product_id;
    }

    // Set initial variant (first available or first)
    const initialVariant = variants.find(v => v.available) || variants[0];
    if (initialVariant) {
      updateVariantState(initialVariant);
    }

    return true;
  }

  // ============================================================================
  // VARIANT CHANGE DETECTION
  // ============================================================================

  /**
   * Handle variant change from theme's variant-change event
   */
  function handleThemeVariantChange(event) {
    if (!event || !event.data || !event.data.variant) return;

    const variant = event.data.variant;
    updateVariantState(variant);
    
    dispatchEvent(EVENTS.variantChange, {
      variant: state.selectedVariant,
      productId: state.productId,
      sectionId: event.data.sectionId
    });
  }

  /**
   * Handle variant change from DOM (fallback)
   */
  function handleDomVariantChange() {
    const options = getSelectedOptions();
    if (!options) return;

    const variant = findVariantByOptions(options);
    if (!variant) return;

    // Only update if variant actually changed
    if (state.selectedVariantId === variant.id) return;

    updateVariantState(variant);
    
    dispatchEvent(EVENTS.variantChange, {
      variant: state.selectedVariant,
      productId: state.productId,
      sectionId: null
    });
  }

  /**
   * Setup variant change listeners
   */
  function setupVariantListeners() {
    const productRoot = getClosestProductRoot(document);
    if (!productRoot) return;

    // Listen to theme's variant-change event (pubsub)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleThemeVariantChange);
    }

    // Fallback: listen to variant input changes
    const variantRadios = productRoot.querySelector('variant-radios');
    const variantSelects = productRoot.querySelector('variant-selects');

    if (variantRadios) {
      // Listen to radio changes
      const radios = variantRadios.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.addEventListener('change', handleDomVariantChange);
      });
    }

    if (variantSelects) {
      // Listen to select changes
      const selects = variantSelects.querySelectorAll('select');
      selects.forEach(select => {
        select.addEventListener('change', handleDomVariantChange);
      });
    }

    // Also listen to direct variant input changes (name="id")
    const variantInput = getProductForm()?.querySelector(SELECTORS.variantInput);
    if (variantInput) {
      variantInput.addEventListener('change', function() {
        const variantId = parseInt(this.value, 10);
        const variant = findVariantById(variantId);
        if (variant) {
          updateVariantState(variant);
          dispatchEvent(EVENTS.variantChange, {
            variant: state.selectedVariant,
            productId: state.productId,
            sectionId: null
          });
        }
      });
    }
  }

  // ============================================================================
  // QUANTITY HANDLING
  // ============================================================================

  /**
   * Setup quantity change listeners
   */
  function setupQuantityListeners() {
    const productForm = getProductForm();
    if (!productForm) return;

    const quantityInput = productForm.querySelector(SELECTORS.quantityInput);
    if (!quantityInput) return;

    // Listen to quantity input changes
    quantityInput.addEventListener('change', function() {
      const qty = parseInt(this.value, 10) || 1;
      setQuantity(qty);
    });

    // Also listen to input events for real-time updates
    quantityInput.addEventListener('input', throttle(function() {
      const qty = parseInt(this.value, 10) || 1;
      if (qty !== state.quantity) {
        updateQuantityState(qty);
        dispatchEvent(EVENTS.quantityChange, {
          quantity: state.quantity
        });
      }
    }, 300));
  }

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================

  /**
   * Add items to cart via AJAX
   * Used by Bundle Offer and Sticky ATC
   */
  function addToCart(itemsArray) {
    if (!itemsArray || !Array.isArray(itemsArray) || itemsArray.length === 0) {
      const error = { message: 'Invalid items array' };
      dispatchEvent(EVENTS.cartError, { error });
      return Promise.reject(error);
    }

    // Validate items format
    const validItems = itemsArray.map(item => {
      if (typeof item === 'object' && item.id && item.quantity) {
        return {
          id: item.id,
          quantity: parseInt(item.quantity, 10) || 1,
          properties: item.properties || {}
        };
      }
      return null;
    }).filter(Boolean);

    if (validItems.length === 0) {
      const error = { message: 'No valid items to add' };
      dispatchEvent(EVENTS.cartError, { error });
      return Promise.reject(error);
    }

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: validItems })
    };

    return fetch('/cart/add.js', config)
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => Promise.reject(err));
        }
        return response.json();
      })
      .then(data => {
        dispatchEvent(EVENTS.cartAdded, {
          items: validItems,
          response: data
        });
        return data;
      })
      .catch(error => {
        dispatchEvent(EVENTS.cartError, { error });
        throw error;
      });
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize Pulse Product Infrastructure
   */
  function init() {
    // Only run on product pages
    if (!document.querySelector(SELECTORS.productRoot) && 
        !document.querySelector('product-info') &&
        !document.querySelector('main-product')) {
      return;
    }

    // Prevent double initialization
    if (state.initialized) {
      console.warn('PulseProduct: Already initialized');
      return;
    }

    // Load variants JSON
    if (!loadVariantsJson()) {
      console.warn('PulseProduct: Failed to load variants, some features may not work');
      // Don't return - still initialize for single-variant products
    }

    // Setup listeners
    setupVariantListeners();
    setupQuantityListeners();

    // Get initial quantity from form
    const productForm = getProductForm();
    if (productForm) {
      const quantityInput = productForm.querySelector(SELECTORS.quantityInput);
      if (quantityInput) {
        const initialQty = parseInt(quantityInput.value, 10) || 1;
        updateQuantityState(initialQty);
      }
    }

    // Try to get money format from Shopify
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      state.moneyFormat = '{{amount}}'; // Default Shopify format
      state.currencyFormat = Shopify.currency?.active || null;
    }

    state.initialized = true;

    // Dispatch initial variant change event
    if (state.selectedVariant) {
      dispatchEvent(EVENTS.variantChange, {
        variant: state.selectedVariant,
        productId: state.productId,
        sectionId: null
      });
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  const PulseProduct = {
    // State (read-only access)
    get state() {
      return Object.freeze({ ...state });
    },

    // Initialization
    init: init,

    // Variant management
    setVariant: function(variantIdOrVariant) {
      let variant = null;
      
      if (typeof variantIdOrVariant === 'number') {
        variant = findVariantById(variantIdOrVariant);
      } else if (typeof variantIdOrVariant === 'object' && variantIdOrVariant.id) {
        variant = variantIdOrVariant;
      }

      if (variant) {
        updateVariantState(variant);
        dispatchEvent(EVENTS.variantChange, {
          variant: state.selectedVariant,
          productId: state.productId,
          sectionId: null
        });
      }
    },

    getSelectedOptions: getSelectedOptions,
    findVariantByOptions: findVariantByOptions,
    findVariantById: findVariantById,

    // Quantity management
    setQuantity: function(qty) {
      const quantity = parseInt(qty, 10) || 1;
      updateQuantityState(quantity);
      dispatchEvent(EVENTS.quantityChange, {
        quantity: state.quantity
      });
    },

    // Money formatting
    formatMoney: formatMoney,

    // Cart operations
    addToCart: addToCart,

    // DOM helpers
    getClosestProductRoot: getClosestProductRoot,
    getProductForm: getProductForm,

    // Events (for reference)
    EVENTS: Object.freeze({ ...EVENTS })
  };

  // ============================================================================
  // EXPOSE GLOBAL API
  // ============================================================================

  window.PulseProduct = PulseProduct;

  // ============================================================================
  // AUTO-INITIALIZE
  // ============================================================================

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  // Re-initialize on dynamic content updates (e.g., theme editor)
  if (typeof document.addEventListener !== 'undefined') {
    // Listen for section:load events (Shopify theme editor)
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail?.sectionId && document.querySelector(`#MainProduct-${event.detail.sectionId}`)) {
        state.initialized = false;
        init();
      }
    });
  }

})();

